import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Tabs, Tab, Badge,
    Grid, Card, CardContent,
} from '@mui/material';
import {
    AccessTime as AccessTimeIcon,
    AddCircleOutline as AddCircleOutlineIcon,
    Dashboard as OverviewIcon,
    FormatListBulleted as AllListIcon,
    BarChart as AnalyticsIcon,
    Schedule as ScheduleIcon,
    HourglassEmpty as PendingIcon,
    CheckCircleOutline as ApprovedIcon,
    CancelOutlined as RejectedIcon,
} from '@mui/icons-material';

// ── Personal timesheet sub-components (re-used from Employee) ─────────────────
import TimesheetList from '../employee/myTimesheet/timesheetList';
import TimesheetForm from '../employee/myTimesheet/timesheetForm';

// ── Team review sub-components (re-used from Manager) ─────────────────────────
import TeamTimesheetList from '../manager/timesheetManagement/teamTimesheetList';
import TimesheetAnalytics from '../manager/timesheetManagement/timesheetAnalytics';
import ManagerTimesheetOverview from '../manager/timesheetManagement/managerTimesheetOverview';

import { getData, postData } from '../../services/axiosInstance';
import { API_ENDPOINTS } from '../../services/endpoints';
import { getUserData } from '../../utils/auth';
import Loader from '../../common/spinner';

// ─── Personal timesheet stats defaults ───────────────────────────────────────
const DEFAULT_STATS = {
    totalHours: 0,
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
};

// ─── Team timesheet normalizers (same logic as ManagerTimesheets) ─────────────
const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function normalizeEntry(e) {
    const dateStr = e.entry_date || e.date || '';
    const dayAbbr = dateStr
        ? DAY_ABBR[new Date(dateStr + 'T00:00:00').getDay()]
        : (e.day || '');

    let attachments = e.attachments || [];
    if (!attachments.length && e.attachment_urls) {
        const raw = e.attachment_urls;
        if (Array.isArray(raw)) attachments = raw;
        else if (typeof raw === 'string') {
            try { attachments = JSON.parse(raw); } catch { attachments = []; }
        }
    }

    return {
        ...e,
        date: dateStr,
        day: dayAbbr,
        dayType: e.day_type || e.dayType || 'Working',
        project: e.project_name || e.project || '',
        task: e.task_name || e.task || '',
        description: e.work_description || e.description || '',
        hours: parseFloat(e.hours_worked ?? e.hours ?? 0),
        overtime: parseFloat(e.overtime_hours ?? e.overtime ?? 0),
        billable: e.is_billable !== undefined
            ? (e.is_billable ? 'Billable' : 'Non-Billable')
            : (e.billable || 'Non-Billable'),
        location: e.location || e.work_location || '',
        leaveType: e.leave_type || e.leaveType || '',
        attachments,
    };
}

function normalizeTimesheet(ts) {
    return {
        ...ts,
        employeeDept: ts.department_name || ts.employeeDept || '',
        employeeTeam: ts.team_name || ts.employeeTeam || '',
        managerName: ts.level1_approver_name || ts.managerName || '',
        approvedBy: ts.level1_approver_name || ts.rejected_by_name || ts.approvedBy || '',
        approvedOn: ts.approvedOn || '',
        entries: (ts.entries || []).map(normalizeEntry),
    };
}

// ─── Tab IDs ──────────────────────────────────────────────────────────────────
const MY_TIMESHEETS_TAB = 0;
const LOG_TIME_TAB      = 1;
const TEAM_OVERVIEW_TAB = 2;
const TEAM_LIST_TAB     = 3;
const ANALYTICS_TAB     = 4;

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TeamLeadTimesheets() {
    const location = useLocation();
    const navigate = useNavigate();

    // ── Personal timesheet state ──────────────────────────────────────────────
    const [personalStats, setPersonalStats] = useState(DEFAULT_STATS);
    const [statsLoading, setStatsLoading] = useState(true);
    const [editData, setEditData] = useState(null);

    // ── Team timesheet state ──────────────────────────────────────────────────
    const [timesheets, setTimesheets]     = useState([]);
    const [teamMembers, setTeamMembers]   = useState([]);
    const [teamLoading, setTeamLoading]   = useState(true);
    const [teamError, setTeamError]       = useState(null);
    const [teamListFilter, setTeamListFilter] = useState('all');

    // ── Active top-level tab ──────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState(location.state?.returnTab ?? MY_TIMESHEETS_TAB);

    // ── Fetch personal timesheet stats ───────────────────────────────────────
    const fetchPersonalStats = useCallback(async () => {
        const userData = getUserData();
        const userId = userData?.id;
        if (!userId) { setStatsLoading(false); return; }

        setStatsLoading(true);
        try {
            const res = await getData(API_ENDPOINTS.getTimesheetStats(userId));
            if (res?.status === 'SUCCESS') {
                const payload = res.result?.data ?? {};
                setPersonalStats({
                    totalHours:   payload.totalHoursThisMonth ?? 0,
                    pendingCount: payload.pendingCount ?? 0,
                    approvedCount: payload.approvedCount ?? 0,
                    rejectedCount: payload.rejectedCount ?? 0,
                });
            } else {
                setPersonalStats(DEFAULT_STATS);
            }
        } catch {
            setPersonalStats(DEFAULT_STATS);
        } finally {
            setStatsLoading(false);
        }
    }, []);

    // ── Fetch team timesheets ────────────────────────────────────────────────
    const fetchTeamTimesheets = useCallback(async () => {
        setTeamLoading(true);
        setTeamError(null);
        try {
            const url = API_ENDPOINTS.getTeamTimesheets('', '', '', 'all', '', 1, 500);
            const res = await getData(url);
            const raw = res?.result?.data || [];
            const normalized = raw.map(normalizeTimesheet);
            setTimesheets(normalized);

            const seen = {};
            normalized.forEach(ts => {
                const key = String(ts.employeeId);
                if (ts.employeeId && !seen[key]) {
                    seen[key] = {
                        id: key,
                        name: ts.employeeName,
                        avatar: ts.employeeAvatar,
                        role: ts.employeeRole,
                        dept: ts.employeeDept,
                    };
                }
            });
            setTeamMembers(Object.values(seen));
        } catch (err) {
            console.error('Failed to fetch team timesheets:', err);
            setTeamError('Failed to load team timesheets. Please try again.');
        } finally {
            setTeamLoading(false);
        }
    }, []);

    useEffect(() => { fetchPersonalStats(); }, [fetchPersonalStats]);
    useEffect(() => { fetchTeamTimesheets(); }, [fetchTeamTimesheets]);

    // ── Team action handlers ─────────────────────────────────────────────────
    const handleTeamAction = useCallback(async (id, decision, comment) => {
        if (decision === 'SendToL2') { await fetchTeamTimesheets(); return; }
        const url = decision === 'Approve'
            ? API_ENDPOINTS.approveTimesheet(id)
            : API_ENDPOINTS.rejectTimesheet(id);
        await postData(url, { comment: comment || '' });
        await fetchTeamTimesheets();
    }, [fetchTeamTimesheets]);

    const handleBulkApprove = useCallback(async (ids) => {
        await postData(API_ENDPOINTS.bulkApproveTimesheets, { timesheet_ids: ids });
        await fetchTeamTimesheets();
    }, [fetchTeamTimesheets]);

    const handleBulkReject = useCallback(async (ids, comment) => {
        await postData(API_ENDPOINTS.bulkRejectTimesheets, { timesheet_ids: ids, comment });
        await fetchTeamTimesheets();
    }, [fetchTeamTimesheets]);

    const handleTeamTabChange = useCallback((tab, filter) => {
        setActiveTab(TEAM_OVERVIEW_TAB + tab);
        setTeamListFilter(filter !== undefined ? filter : 'all');
    }, []);

    // ── Personal timesheet handlers ──────────────────────────────────────────
    const handleEdit = (timesheet) => {
        setEditData(timesheet);
        setActiveTab(LOG_TIME_TAB);
    };

    const handleFormDone = () => {
        setEditData(null);
        setActiveTab(MY_TIMESHEETS_TAB);
        fetchPersonalStats();
    };

    const handleTabChange = (_, newValue) => {
        setActiveTab(newValue);
        if (newValue !== LOG_TIME_TAB) setEditData(null);
    };

    // ── Derived counts ───────────────────────────────────────────────────────
    const pendingTeamCount = timesheets.filter(t => t.status === 'Submitted').length;

    const STAT_CARDS = [
        { label: 'Total Hours This Month', value: personalStats.totalHours, suffix: 'hrs', icon: ScheduleIcon, gradient: 'linear-gradient(135deg, #818CF8 0%, #6366F1 100%)' },
        { label: 'Pending Approvals',      value: personalStats.pendingCount,  icon: PendingIcon,  gradient: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)' },
        { label: 'Approved Timesheets',    value: personalStats.approvedCount, icon: ApprovedIcon, gradient: 'linear-gradient(135deg, #34D399 0%, #10B981 100%)' },
        { label: 'Rejected Timesheets',    value: personalStats.rejectedCount, icon: RejectedIcon, gradient: 'linear-gradient(135deg, #F87171 0%, #EF4444 100%)' },
    ];

    if (statsLoading && teamLoading) return <Loader />;

    return (
        <Box sx={{ p: 1 }}>
            {/* ── Page Header ──────────────────────────────────────────────── */}
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                Timesheets
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 2.5 }}>
                Log your own hours and review your team's weekly timesheets
            </Typography>

            {/* ── Tab Bar ──────────────────────────────────────────────────── */}
            <Box sx={{
                bgcolor: '#fff', borderRadius: '12px',
                boxShadow: '0px 2px 4px rgba(0,0,0,0.05)',
                border: '1px solid #e2e8f0', p: 0.5, mb: 3,
            }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        minHeight: 44,
                        '& .MuiTabs-indicator': { display: 'none' },
                        '& .MuiTab-root': {
                            textTransform: 'none', fontWeight: 600, fontSize: 13,
                            minHeight: 40, borderRadius: 2, color: '#64748b',
                            '&.Mui-selected': { color: '#6366f1', bgcolor: '#eef2ff' },
                            '&:hover': { bgcolor: '#f8fafc' },
                        },
                    }}
                >
                    {/* ── Personal tabs ── */}
                    <Tab
                        icon={<AccessTimeIcon sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label="My Timesheets"
                    />
                    <Tab
                        icon={<AddCircleOutlineIcon sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label="Log Time"
                    />

                    {/* ── Team tabs ──
                    <Tab
                        icon={<OverviewIcon sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label="Team Overview"
                    />
                    <Tab
                        icon={<AllListIcon sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                Team Timesheets
                                {pendingTeamCount > 0 && (
                                    <Badge
                                        badgeContent={pendingTeamCount}
                                        color="warning"
                                        sx={{ '& .MuiBadge-badge': { fontSize: 10, fontWeight: 700, minWidth: 18, height: 18 } }}
                                    >
                                        <Box sx={{ width: 4 }} />
                                    </Badge>
                                )}
                            </Box>
                        }
                    />
                    <Tab
                        icon={<AnalyticsIcon sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label="Analytics"
                    /> */}
                </Tabs>
            </Box>

            {/* ══ MY TIMESHEETS TAB ══════════════════════════════════════════ */}
            {activeTab === MY_TIMESHEETS_TAB && (
                <>
                    {/* Stat cards */}
                    <Grid container spacing={2.5} sx={{ mb: 3 }}>
                        {STAT_CARDS.map(card => (
                            <Grid size={{ xs: 12, sm: 3 }} key={card.label} sx={{ display: 'flex' }}>
                                <Card sx={{
                                    display: 'flex', flexDirection: 'column',
                                    width: '100%', height: '100%',
                                    borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', overflow: 'hidden',
                                }}>
                                    <CardContent sx={{
                                        flexGrow: 1, background: card.gradient,
                                        display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5, lg: 2 },
                                        py: { xs: 1.5, sm: 2, lg: 2.5 }, px: { xs: 1.5, sm: 2, lg: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2, lg: 2.5 } },
                                    }}>
                                        <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 1, display: 'flex', color: '#fff', flexShrink: 0 }}>
                                            <card.icon />
                                        </Box>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography sx={{ fontSize: { xs: 12, sm: 13 }, color: 'rgba(255,255,255,0.85)', fontWeight: 500, lineHeight: 1.3, wordBreak: 'break-word' }}>
                                                {card.label}
                                            </Typography>
                                            <Typography sx={{ fontSize: { xs: 20, sm: 22, md: 24, lg: 26 }, fontWeight: 700, color: '#fff', lineHeight: 1.2, wordBreak: 'break-word' }}>
                                                {card.value}
                                                {card.suffix && (
                                                    <Typography component="span" sx={{ fontSize: { xs: 12, sm: 13, lg: 14 }, fontWeight: 400, ml: 0.5 }}>
                                                        {card.suffix}
                                                    </Typography>
                                                )}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    <TimesheetList onEdit={handleEdit} />
                </>
            )}

            {/* ══ LOG TIME TAB ══════════════════════════════════════════════ */}
            {activeTab === LOG_TIME_TAB && (
                <TimesheetForm editData={editData} onDone={handleFormDone} />
            )}

            {/* ══ TEAM OVERVIEW TAB ═════════════════════════════════════════ */}
            {activeTab === TEAM_OVERVIEW_TAB && (
                teamLoading
                    ? <Loader />
                    : <ManagerTimesheetOverview timesheets={timesheets} onTabChange={handleTeamTabChange} />
            )}

            {/* ══ TEAM TIMESHEETS TAB ═══════════════════════════════════════ */}
            {activeTab === TEAM_LIST_TAB && (
                <TeamTimesheetList
                    timesheets={timesheets}
                    teamMembers={teamMembers}
                    loading={teamLoading}
                    filterStatus={teamListFilter}
                    onAction={handleTeamAction}
                    onBulkApprove={handleBulkApprove}
                    onBulkReject={handleBulkReject}
                />
            )}

            {/* ══ ANALYTICS TAB ════════════════════════════════════════════ */}
            {activeTab === ANALYTICS_TAB && (
                <TimesheetAnalytics timesheets={timesheets} teamMembers={teamMembers} />
            )}
        </Box>
    );
}
